import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ShoppingCart, ChevronRight } from "lucide-react";
import { api, formatR, CATEGORY_NAMES } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { BackButton } from "@/components/BackButton";

export default function ProductPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState("");
  const [variantError, setVariantError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const { add, setDrawerOpen } = useCart();

  useEffect(() => {
    setData(null);
    setQty(1);
    setVariant("");
    setVariantError(false);
    setNotFound(false);
    api
      .get(`/products/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="product-not-found">
        <h1 className="font-display text-2xl font-bold text-navy">Product not found</h1>
        <Link to="/shop" className="text-brand font-semibold text-sm mt-3 inline-block" data-testid="back-to-shop">
          Back to the shop
        </Link>
      </div>
    );

  if (!data)
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="aspect-square bg-white rounded-3xl animate-pulse" />
          <div className="space-y-4 pt-6">
            <div className="h-8 bg-slate-100 rounded w-3/4 animate-pulse" />
            <div className="h-5 bg-slate-100 rounded w-1/3 animate-pulse" />
          </div>
        </div>
      </div>
    );

  const p = data.product;
  const discount = p.on_special ? Math.round((1 - p.effective_price / p.price) * 100) : 0;
  const variantGroup = (p.variants || [])[0];

  const addToCart = () => {
    if (variantGroup && (variantGroup.required ?? true) && !variant) {
      setVariantError(true);
      return;
    }
    add(p, qty, { variant: variant || undefined });
    setDrawerOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="product-page">
      <Seo title={p.name} description={(p.description || `Buy ${p.name} online at BTMEDU.`).slice(0, 158)} />
      <div className="mb-4">
        <BackButton label="Back" testId="product-back-button" />
      </div>
      <nav className="flex items-center gap-1.5 text-xs text-slate-500 mb-8" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-navy">Home</Link>
        <ChevronRight size={12} />
        <Link to={`/shop/${p.category}`} className="hover:text-navy">{CATEGORY_NAMES[p.category]}</Link>
        <ChevronRight size={12} />
        <span className="text-navy font-medium truncate max-w-[200px]">{p.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden border border-slate-200/70 spotlight-img bg-white">
            <img src={p.image} alt={p.name} className="w-full aspect-square object-cover" />
            {p.on_special && (
              <span className="absolute top-4 left-4 bg-brand text-white text-xs font-bold px-3 py-1.5 rounded-full" data-testid="product-special-badge">
                SAVE {discount}%
              </span>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{CATEGORY_NAMES[p.category]}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy mt-2 text-balance" data-testid="product-name">
            {p.name}
          </h1>
          <div className="flex items-center gap-4 mt-3 text-xs font-mono text-slate-500">
            {p.sku && <span data-testid="product-sku">SKU {p.sku}</span>}
            {p.isbn && <span data-testid="product-isbn">ISBN {p.isbn}</span>}
          </div>
          <div className="mt-5 flex items-end gap-3">
            {p.on_special && <span className="text-lg text-slate-400 line-through font-mono">{formatR(p.price)}</span>}
            <span className="text-3xl font-extrabold text-navy font-mono" data-testid="product-price">
              {p.effective_price > 0 ? formatR(p.effective_price) : "Price on request"}
            </span>
          </div>
          <p className={`mt-3 text-sm font-semibold ${p.in_stock ? "text-emerald-600" : "text-red-500"}`} data-testid="product-stock">
            {p.in_stock ? `In stock — ${p.stock} available` : "Out of stock"}
          </p>
          <p className="mt-5 text-sm sm:text-base text-slate-600 leading-relaxed">{p.description}</p>

          {variantGroup && (
            <div className="mt-6" data-testid="variant-picker">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-navy mb-1.5" htmlFor="variant-select">
                {variantGroup.label} <span className="text-brand">*</span>
              </label>
              <select
                id="variant-select"
                value={variant}
                onChange={(e) => { setVariant(e.target.value); setVariantError(false); }}
                className={`w-full max-w-xs h-12 rounded-xl border px-4 text-sm font-semibold text-navy bg-white outline-none focus:ring-2 focus:ring-gold ${variantError ? "border-rose-400 ring-2 ring-rose-200" : "border-slate-200"}`}
                data-testid="variant-select"
              >
                <option value="">Choose…</option>
                {(variantGroup.options || []).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
              {variantError && (
                <p className="text-xs text-rose-500 font-semibold mt-1.5" data-testid="variant-error">
                  Please choose: {variantGroup.label}
                </p>
              )}
            </div>
          )}

          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <div className="flex items-center border border-slate-200 rounded-full bg-white">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-11 flex items-center justify-center" data-testid="qty-minus" aria-label="Decrease quantity">
                <Minus size={15} />
              </button>
              <span className="w-8 text-center font-bold text-navy" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(p.stock || 99, qty + 1))} className="w-11 h-11 flex items-center justify-center" data-testid="qty-plus" aria-label="Increase quantity">
                <Plus size={15} />
              </button>
            </div>
            <button
              onClick={addToCart}
              disabled={!p.in_stock}
              className="h-12 px-8 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-40 text-white font-bold text-sm flex items-center gap-2 transition-colors gold-glow"
              data-testid="product-add-to-cart"
            >
              <ShoppingCart size={16} /> Add to cart
            </button>
          </div>
          <div className="mt-8 bg-cream rounded-2xl p-5 text-sm text-slate-600 space-y-1.5">
            <p><span className="font-semibold text-navy">Delivery:</span> courier nationwide, free over R750.</p>
            <p><span className="font-semibold text-navy">Collection:</span> pick up in store once your order is confirmed ready.</p>
          </div>
        </Reveal>
      </div>

      {data.related.length > 0 && (
        <section className="mt-20" data-testid="related-products">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-navy">You may also need</h2>
          </Reveal>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {data.related.map((r, i) => (
              <ProductCard key={r.id} product={r} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
